import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import style from "./surgeon.module.css";
import {
  useGetSurgeonDetailsQuery,
  useUpdateSurgeonDefaultAddressMutation,
  useAddDoctorDeliveryAddressMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import AddressDetails from "./components/AddressDetails";
import { RootState, useAppSelector } from "../../store";
import AddressForm from "./components/AddressForm";
import { DEFAULT_SUPER_ADMIN_ROLE_ID } from "../../utils/constants";

/**
 * Displays surgeon details, delivery addresses, and allows adding/updating default addresses.
 *
 * Returns:
 *   JSX.Element: Surgeon details view.
 *
 * Notes:
 *   Relies on route param `id` and health org context for data fetching.
 *   Mutations use RTK Query and expect server-side validation; local error state is shown via LabelBox.
 */
const SurgeonDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useAppSelector(
    (state: RootState) => state.auth
  );
  const { specialities } = useAppSelector(
    (state: RootState) => state.referenceData
  );

  const [isAddNewAddress, setIsAddNewAddress] = useState(false);
  const [isSavingAddressDetails, setIsSavingAddressDetails] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const isSuperAdmin =
    userDetails?.user_role_id === DEFAULT_SUPER_ADMIN_ROLE_ID;

  // Location state can override health org context for cross-org views.
  const locationState =
    (location.state as { healthOrgId?: number } | null) ?? null;
  const healthOrgId =
    userDetails?.health_org_id ?? locationState?.healthOrgId ?? 0;
  const canAddAddress = Boolean(healthOrgId);

  const {
    data: surgeonDetails,
    isLoading,
    isError,
    error,
    refetch: getSurgeonDetails,
  } = useGetSurgeonDetailsQuery({ id: id ?? "", healthOrgId });
  const speciality = specialities.find((speciality) => speciality.id === (surgeonDetails?.speciality_id ?? 0));

  const [updateSurgeonDefaultAddress] = useUpdateSurgeonDefaultAddressMutation();

  const [addDoctorDeliveryAddress] = useAddDoctorDeliveryAddressMutation();

  /**
   * Normalizes delivery addresses into a consistent array shape for rendering.
   *
   * Returns:
   *   AddressDetails[]: Sanitized address list (no falsy entries).
   *
   * Notes:
   *   - Backend may return a single address or an array.
   *   - TODO: Confirm whether `delivery_address` can be null or mixed types.
   */
  // Normalize backend response into a consistent array for UI rendering.
  const mappedAddresses: AddressDetails[] = useMemo(() => {
    const address = surgeonDetails?.delivery_address ?? [];
    if (!address) return [];
    const addressArray = Array.isArray(address) ? address : [address];
    const filtered = addressArray.filter(
      (addr): addr is AddressDetails => !!addr
    );
    return filtered.map((addr) => ({
      id: addr.id,
      unit_number: addr.unit_number ?? undefined,
      street_number: addr.street_number ?? undefined,
      street_name: addr.street_name ?? undefined,
      suburb: addr.suburb ?? undefined,
      state: addr.state ?? undefined,
      postcode: addr.postcode ?? undefined,
      mail: addr.mail ?? undefined,
      phone: addr.phone ?? undefined,
      fax: addr.fax ?? undefined,
      provider_number: addr.provider_number ?? undefined,
    }));
  }, [surgeonDetails]);

  /**
   * Sets the default delivery address for a surgeon.
   *
   * Args:
   *   addressPayload (UpdateDeliveryAddressPayload): Payload including doctor and address IDs.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   *   - Requires doctor_id, health_org_id, and doctor_delivery_address_id.
   */
  // Default address updates require ids for surgeon, address, and org linkage.
  const updateDefaultAddress = async (
    addressPayload: UpdateDeliveryAddressPayload
  ) => {
    if (
      !addressPayload.doctor_id ||
      !addressPayload.doctor_delivery_address_id ||
      !addressPayload.health_org_id
    ) {
      setMessage({
        type: "error",
        text: "Something went wrong. Please try again later.",
      });
      return;
    }
    setIsSavingAddressDetails(true);
    try {
      await updateSurgeonDefaultAddress(addressPayload).unwrap();
      await getSurgeonDetails();
      setIsAddNewAddress(false);
    } catch (error: any) {
      setMessage({
        type: "error",
        text:
          error?.message?.toString() ||
          "Something went wrong. Please try again later.",
      });
    } finally {
      setIsSavingAddressDetails(false);
    }
  };

  /**
   * Adds a new delivery address for the surgeon.
   *
   * Args:
   *   addressPayload (DeliveryAddressPayload): Address details to create.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   *   - Requires surgeon id and health org context to link the address.
   */
  // Address creation must include org context for backend linkage.
  const addNewAddress = async (addressPayload: DeliveryAddressPayload) => {
    try {
      setIsSavingAddressDetails(true);
      if (!surgeonDetails?.id || !healthOrgId) {
        setMessage({
          type: "error",
          text: "Please enter valid details",
        });
        setIsSavingAddressDetails(false);
        return;
      }
      const doctor_id = Number(surgeonDetails?.id ?? 0);
      const health_org_id = healthOrgId;
      // Health org context and association flag are required for backend linkage; omit and the address won't attach.
      await addDoctorDeliveryAddress({
        doctor_id,
        health_org_id: health_org_id,
        is_associated: true,
        address: addressPayload,
      }).unwrap();
      setIsAddNewAddress(false);
      setIsSavingAddressDetails(false);
      await getSurgeonDetails();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error?.message?.toString() || "Please enter valid details",
      });
      setIsSavingAddressDetails(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "0px 20px" }}>
            {isError && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={{
                    type: "error",
                    text:
                      (error as any)?.data?.detail?.toString() ||
                      (error as any)?.error?.toString() ||
                      "Unable to load surgeon details.",
                  }}
                  onClose={() => {}}
                />
              </Box>
            )}
            <Typography className={style.surgeonDetailHeading}>
              Surgeon Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Full Name</Typography>
                <Typography className={style.value}>
                  {(() => {
                    const lastName =
                      surgeonDetails?.surname ?? "";
                    const firstName =
                      surgeonDetails?.given_name ?? "";
                    const prefix = surgeonDetails?.prefix ?? "";
                    return `${(lastName || "").toUpperCase()}, ${firstName} ${prefix}`;
                  })()}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Speciality</Typography>
                <Typography className={style.value}>
                  {speciality?.name || "-"}
                </Typography>
              </Box>
              <Box sx={{ flexDirection: "column" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    className={style.surgeonDetailHeading}
                    sx={{ fontSize: "20px !important" }}
                  >
                    Report Delivery Address Details
                  </Typography>
                  {canAddAddress && surgeonDetails && (
                    <Button
                      variant="contained"
                      label={"Add New Address"}
                      sx={{
                        ":hover": {
                          backgroundColor: colors.primary,
                        },
                      }}
                      onClick={() => {
                        setIsAddNewAddress(true);
                      }}
                    />
                  )}
                </Box>
                <Box style={{ marginTop: 10 }}>
                  {mappedAddresses &&
                    mappedAddresses.map((address, index) => {
                      const isDefault = address.id === surgeonDetails?.default_delivery_address_id;
                      return (
                        <Box key={address.id ?? index}>
                          <AddressDetails
                            address={address}
                            isDefault={isDefault}
                            hideDefaultToggle={isSuperAdmin}
                            onUpdateDefaultAddress={() => {
                              const payload: UpdateDeliveryAddressPayload = {
                                doctor_delivery_address_id: Number(
                                  address.id ?? "0"
                                ),
                                doctor_id: Number(surgeonDetails?.id ?? 0),
                                health_org_id: healthOrgId,
                                is_associated: true,
                              };
                              updateDefaultAddress(payload);
                            }}
                          />
                        </Box>
                      );
                    })}
                </Box>
                {isAddNewAddress && surgeonDetails && (
                  <Box sx={{ marginTop: "20px" }}>
                    <AddressForm
                      onCancel={() => {
                        setIsAddNewAddress(false);
                      }}
                      onSave={(deliveryAddress: DeliveryAddressPayload) => {
                        const payload: DeliveryAddressPayload = {
                          ...deliveryAddress,
                          doctor_id: surgeonDetails?.id ?? 0,
                        };
                        addNewAddress(payload);
                      }}
                      isLoading={isSavingAddressDetails}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {surgeonDetails?.deactivate_reason && (
              <Box>
                <Typography className={style.surgeonDetailHeading}>
                  Deactivation Details
                </Typography>
                <Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Reason for deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {surgeonDetails?.deactivate_reason}
                    </Typography>
                  </Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {surgeonDetails?.deactivated_at
                        ? dayjs(surgeonDetails?.deactivated_at).format(
                          "MMMM DD YYYY, hh:mm:ss A"
                        )
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            {!isAddNewAddress && (
              <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: "transparent" },
                  }}
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  label={"Close"}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SurgeonDetails;
